import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { IconButton } from '@/components/primitives/icon-button'
import { cn } from '@/lib/cn'

export interface ModalProps {
  bodyClassName?: string
  children: ReactNode
  className?: string
  closeLabel: string
  footer?: ReactNode
  footerClassName?: string
  onClose: () => void
  open: boolean
  subtitle?: string
  title: string
  titleActions?: ReactNode
  titleHelp?: string
  titleVisuallyHidden?: boolean
}

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'summary',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusableElements(dialog: HTMLElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter((element) => {
    const closedDetails = element.closest('details:not([open])')
    return (
      closedDetails === null ||
      (element.tagName === 'SUMMARY' &&
        element.parentElement === closedDetails &&
        closedDetails.parentElement?.closest('details:not([open])') === null)
    )
  })
}

function activeElementOutsideDialog() {
  if (typeof document === 'undefined' || !(document.activeElement instanceof HTMLElement)) {
    return null
  }
  return document.activeElement.closest('[role="dialog"]') === null ? document.activeElement : null
}

function CloseIcon() {
  return (
    <svg aria-hidden className="size-5" fill="none" viewBox="0 0 20 20">
      <path
        d="m5 5 10 10M15 5 5 15"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

export function Modal({
  bodyClassName,
  children,
  className,
  closeLabel,
  footer,
  footerClassName,
  onClose,
  open,
  subtitle,
  title,
  titleActions,
  titleHelp,
  titleVisuallyHidden = false,
}: ModalProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const [initialRestoreFocusTo] = useState(activeElementOutsideDialog)
  const restoreFocusRef = useRef<HTMLElement | null>(initialRestoreFocusTo)
  const onCloseRef = useRef(onClose)
  const hasSubtitle = Boolean(subtitle)
  const contentTopPadding = titleVisuallyHidden || hasSubtitle ? 'pt-4' : 'pt-3'

  useLayoutEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (open) {
      return undefined
    }

    const rememberFocus = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement && event.target.closest('[role="dialog"]') === null) {
        restoreFocusRef.current = event.target
      }
    }
    const rememberPointerTarget = (event: PointerEvent) => {
      if (event.target instanceof HTMLElement) {
        restoreFocusRef.current = event.target
      }
    }
    if (restoreFocusRef.current === null && document.activeElement instanceof HTMLElement) {
      restoreFocusRef.current = document.activeElement
    }
    document.addEventListener('focusin', rememberFocus)
    document.addEventListener('pointerdown', rememberPointerTarget, true)

    return () => {
      document.removeEventListener('focusin', rememberFocus)
      document.removeEventListener('pointerdown', rememberPointerTarget, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const dialog = dialogRef.current
    const activeElement = document.activeElement
    if (
      restoreFocusRef.current === null &&
      activeElement instanceof HTMLElement &&
      !dialog?.contains(activeElement)
    ) {
      restoreFocusRef.current = activeElement
    }
    if (dialog && !dialog.contains(document.activeElement)) {
      focusableElements(dialog)[0]?.focus()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const openOverlays = document.querySelectorAll<HTMLElement>('[data-overlay-panel]')
      if (!dialog || openOverlays.item(openOverlays.length - 1) !== dialog) {
        return
      }

      if (event.key === 'Escape') {
        const activeElement = document.activeElement
        if (
          activeElement instanceof HTMLElement &&
          activeElement.getAttribute('role') === 'combobox' &&
          activeElement.getAttribute('aria-expanded') === 'true'
        ) {
          return
        }
        event.stopImmediatePropagation()
        onCloseRef.current()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const elements = focusableElements(dialog)
      if (elements.length === 0) {
        event.preventDefault()
        return
      }
      const first = elements[0]
      const last = elements[elements.length - 1]

      const activeElement = document.activeElement
      if (event.shiftKey && (activeElement === first || !dialog.contains(activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (activeElement === last || !dialog.contains(activeElement))) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      const restoreFocusTo = restoreFocusRef.current
      if (restoreFocusTo?.isConnected) {
        restoreFocusTo.focus()
      }
    }
  }, [open])

  if (!open) {
    return null
  }

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-[rgba(17,24,39,0.32)] px-4">
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className={cn(
          'relative grid w-full max-w-[420px] overflow-hidden rounded-lg bg-white shadow-[0_24px_72px_rgba(17,24,39,0.18)]',
          className,
        )}
        data-overlay-panel
        ref={dialogRef}
        role="dialog"
      >
        <IconButton
          aria-label={closeLabel}
          className="absolute top-3 right-3 size-10 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          onClick={onClose}
          size="md"
        >
          <CloseIcon />
        </IconButton>
        <div
          className={cn(
            'px-5 pt-5 pr-14',
            hasSubtitle ? 'pb-2' : 'pb-0',
            titleVisuallyHidden ? 'sr-only' : '',
          )}
        >
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold leading-6 text-[var(--color-text)]" id={titleId}>
              {title}
            </h2>
            {titleActions ? <div className="ml-auto shrink-0">{titleActions}</div> : null}
            {titleHelp && !titleVisuallyHidden ? <TitleHelp label={titleHelp} /> : null}
          </div>
          {subtitle ? (
            <p className="mt-1 text-sm leading-5 text-[var(--color-text-muted)]">{subtitle}</p>
          ) : null}
        </div>
        <div className={cn('px-5 pb-4', contentTopPadding, bodyClassName)}>{children}</div>
        {footer ? (
          <div
            className={cn('flex items-center justify-end gap-3 px-5 pt-2 pb-5', footerClassName)}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

function TitleHelp({ label }: { label: string }) {
  const tooltipId = useId()

  return (
    <span className="group relative inline-flex">
      <span
        aria-describedby={tooltipId}
        aria-label={label}
        className={cn(
          'inline-flex size-4 cursor-help items-center justify-center rounded-full border border-[var(--color-control-border)]',
          'text-[11px] font-semibold leading-none text-[var(--color-text-muted)] transition-colors',
          'hover:border-[rgba(26,28,25,0.32)] hover:text-[var(--color-text)]',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
        )}
        tabIndex={0}
      >
        i
      </span>
      <span
        className="pointer-events-none absolute left-full top-1/2 z-[110] ml-2 hidden w-[244px] -translate-y-1/2 rounded-md bg-[#1f2937] px-2.5 py-1.5 text-xs font-normal leading-5 text-white shadow-[0_10px_24px_rgba(15,23,42,0.18)] group-hover:block group-focus-within:block"
        id={tooltipId}
        role="tooltip"
      >
        {label}
      </span>
    </span>
  )
}
